import {inject, Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, shareReplay} from 'rxjs/operators';
import {FirestoreService} from './firestore.service';

/** `appOffers/walletBhim` — banner shows while now is on or before `endsAt`. */
const WALLET_OFFER_DOC = 'appOffers/walletBhim';

interface FirestoreTime {
  toDate?: () => Date;
  toMillis?: () => number;
  seconds?: number;
}

interface WalletOfferDoc {
  endsAt?: FirestoreTime | string | Date;
}

function endsAtToDate(value: WalletOfferDoc['endsAt']): Date | null {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  if (typeof value.toDate === 'function') {
    return value.toDate();
  }
  if (typeof value.toMillis === 'function') {
    return new Date(value.toMillis());
  }
  if (typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }
  return null;
}

@Injectable({
  providedIn: 'root',
})
export class WalletOfferService {
  private firestoreService = inject(FirestoreService);

  readonly showBanner$: Observable<boolean> = this.firestoreService.doc$<WalletOfferDoc>(WALLET_OFFER_DOC).pipe(
    map((offer) => {
      const endsAt = endsAtToDate(offer?.endsAt);
      return !!endsAt && new Date() <= endsAt;
    }),
    catchError(() => of(false)),
    shareReplay({bufferSize: 1, refCount: true}),
  );
}
